/**
 * Rail for route-level code splitting.
 *
 * Every lazily-loaded route goes through this wrapper so all of them share
 * one loading state and ONE failure story. The failure that matters: a chunk
 * that fails to fetch — flaky connection mid-navigation, or a session that
 * spans a hosting deploy (chunk filenames are content-hashed, so a deploy
 * removes the old files; index.html is no-cache but an already-open tab still
 * holds references to the old hashes). Without this boundary that throws and
 * white-screens the page; with it the user gets a Reload button, and reload
 * fetches the fresh index.html with working chunk URLs. On the native app
 * chunks load from disk inside the bundle, so the failure path is effectively
 * unreachable there — the boundary is web insurance.
 *
 * Usage in App.js:
 *   const Profile = lazyRoute(() => import('./pages/Profile'));
 *   ...
 *   <Route path="/profile" element={<Profile />} />
 */
import React, { Suspense } from 'react';

class ChunkErrorBoundary extends React.Component {
  state = { failed: false, offlineHint: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('Route chunk failed to load:', error);
  }

  // A hard reload with no connectivity lands on the browser's own offline
  // error page (the Chrome dino) — worse than where the user already is. The
  // reload is only issued when the browser reports a connection; otherwise we
  // ask the user to reconnect first. navigator.onLine is a hint, not a
  // guarantee, but a false "online" just reloads back to this same screen.
  handleReload = () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState({ offlineHint: true });
      return;
    }
    window.location.reload();
  };

  render() {
    if (this.state.failed) {
      return (
        <div style={{ padding: '4rem 1.5rem', textAlign: 'center' }}>
          <p style={{ marginBottom: '1rem', color: '#374151' }}>
            This page didn&apos;t load — your connection may have dropped, or the
            app was just updated.
          </p>
          {this.state.offlineHint && (
            <p style={{ marginBottom: '1rem', color: '#b45309', fontWeight: 600 }}>
              You&apos;re offline right now. Reconnect to the internet, then tap
              Reload again.
            </p>
          )}
          <button
            onClick={this.handleReload}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#C89F3C',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const RouteFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '40vh'
    }}
    aria-label="Loading page"
  >
    <div
      style={{
        width: '32px',
        height: '32px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#C89F3C',
        borderRadius: '50%',
        animation: 'lazy-route-spin 0.8s linear infinite'
      }}
    />
    <style>{`@keyframes lazy-route-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export function lazyRoute(importer) {
  const LazyComponent = React.lazy(importer);
  return function LazyRouteWrapper(props) {
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <LazyComponent {...props} />
        </Suspense>
      </ChunkErrorBoundary>
    );
  };
}
